import { ChevronDownIcon, ChevronUpIcon } from '@ctct/summit-icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from 'react';

import { cn } from '../../lib/utils';
import { type createStyleType } from '../../theme/types';
import { usePortalContainer } from '../PortalContainer/PortalContainerContext';

const iconStyles = 'r:h-5 r:w-5 r:shrink-0 r:text-(--icon)';

const SelectScrollUpButton = forwardRef<
  ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'rds-select-scroll-up',
      'r:flex r:cursor-default r:items-center r:justify-center r:py-1',
      className
    )}
    {...props}
  >
    <ChevronUpIcon className={iconStyles} />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = forwardRef<
  ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'rds-select-scroll-down',
      'r:flex r:cursor-default r:items-center r:justify-center r:py-1',
      className
    )}
    {...props}
  >
    <ChevronDownIcon className={iconStyles} />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const themeableSelectContentVars = [
  'r:[--bg:theme(selectContent.bg.default)]',
  'r:[--border:theme(selectContent.border.default)]',
  'r:[--text:theme(selectContent.text.default)]',
  'r:[--item-bg-selected:theme(selectItem.bg.selected)]',
  'r:[--item-text-selected:theme(selectItem.text.selected)]',
  'r:[--separator:theme(selectSeparator.color.default)]',
] as const;
type SelectContentStyle = ReturnType<typeof createStyleType<typeof themeableSelectContentVars>>;

interface SelectContentProps extends Omit<
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content>,
  'position'
> {
  /** Set inline styles, including the component's built-in CSS variables.  */
  style?: SelectContentStyle;
  container?: React.ComponentProps<typeof SelectPrimitive.Portal>['container'];
}

const SelectContent = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>(({ className, children, container, ...props }, ref) => {
  const containerFromContext = usePortalContainer();
  return (
    <SelectPrimitive.Portal container={container ?? containerFromContext}>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'rds-select-content',
          themeableSelectContentVars.join(' '),
          `
          r:[--rds-select-trigger-width:var(--radix-select-trigger-width)]
          r:bg-(--bg)
          r:border-(--border)
          r:text-(--text)
          r:z-overlay-select r:relative r:gap-2 r:max-h-96 r:overflow-hidden
          r:rounded-md r:border r:shadow-md
          r:data-[state=open]:animate-in
          r:data-[state=open]:fade-in-0
          r:data-[state=open]:zoom-in-95
          r:data-[state=closed]:animate-out
          r:data-[state=closed]:fade-out-0
          r:data-[state=closed]:zoom-out-95
          r:data-[side=top]:slide-in-from-bottom-2
          r:data-[side=top]:-translate-y-1
          r:data-[side=right]:slide-in-from-left-2
          r:data-[side=right]:translate-x-1
          r:data-[side=bottom]:slide-in-from-top-2
          r:data-[side=bottom]:translate-y-1
          r:data-[side=left]:slide-in-from-right-2
          r:data-[side=left]:-translate-x-1
          r:w-[var(--rds-select-trigger-width)]
        `,
          className
        )}
        position="popper"
        data-sds-id="select-content"
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport className="r:p-1 r:w-full r:h-[var(--radix-select-trigger-height)]">
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

export { SelectContent };
